import React, { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Mail, ArrowRight, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import Header from '../components/Layout/Header';
import Footer from '../components/Layout/Footer';
import usePageMeta from '../lib/usePageMeta';
import { SUPPORT_EMAIL } from '../content/sitePages';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Keys match the API (routes/contact.py).
const TOPICS = [
  ['general', 'General question'],
  ['support', 'Help with my account or designs'],
  ['billing', 'Billing and payments'],
  ['sales', 'Teams and business plans'],
  ['partnership', 'Partnerships'],
  ['feedback', 'Feedback and ideas'],
  ['privacy', 'Privacy and data requests'],
];
const TOPIC_KEYS = TOPICS.map(([k]) => k);
const EMPTY = { name: '', email: '', topic: 'general', company: '', message: '', website: '' };
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function validate(form) {
  const errors = {};
  if (!form.name.trim()) errors.name = 'Please enter your name.';
  else if (form.name.trim().length > 100) errors.name = 'Please keep your name under 100 characters.';
  if (!EMAIL_RE.test(form.email.trim())) errors.email = 'Please enter a valid email address, so we can reply.';
  if (form.company.trim().length > 120) errors.company = 'Please keep this under 120 characters.';
  const len = form.message.trim().length;
  if (len < 10) errors.message = 'Please write at least 10 characters.';
  else if (len > 5000) errors.message = 'Please keep your message under 5,000 characters.';
  return errors;
}

const inputClass = (bad) =>
  `w-full px-3 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent ${bad ? 'border-red-400' : 'border-gray-200'}`;

function Field({ id, label, required, error, hint, children }) {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1.5">
        {label}{required && <span className="text-red-500" aria-hidden="true"> *</span>}
      </label>
      {children}
      {error ? <p id={`${id}-error`} className="mt-1.5 text-xs text-red-600">{error}</p>
        : hint ? <p className="mt-1.5 text-xs text-gray-400">{hint}</p> : null}
    </div>
  );
}

const ContactPage = () => {
  usePageMeta('Contact', 'Get in touch with iVisora — questions, help with your account or designs, billing, partnerships and privacy requests.', { path: '/contact' });
  const location = useLocation();
  const initialTopic = useMemo(() => {
    const t = new URLSearchParams(location.search).get('topic');
    return TOPIC_KEYS.includes(t) ? t : 'general';
  }, [location.search]);

  const [form, setForm] = useState({ ...EMPTY, topic: initialTopic });
  const [errors, setErrors] = useState({});
  const [state, setState] = useState('idle');           // idle | sending | sent | failed
  const [failure, setFailure] = useState(null);         // what to tell the visitor when sending fails
  const [sentTo, setSentTo] = useState('');

  const set = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (state === 'sending') return;
    const found = validate(form);
    setErrors(found);
    if (Object.keys(found).length) return;

    setState('sending');
    setFailure(null);
    try {
      const res = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          topic: form.topic,
          company: form.company.trim() || null,
          message: form.message.trim(),
          website: form.website,
        }),
      });
      if (res.ok) {
        setSentTo(form.email.trim());
        setState('sent');
        return;
      }
      setFailure(res.status === 429 ? 'rate' : res.status === 422 ? 'invalid' : 'error');
    } catch {
      setFailure('error');
    }
    setState('failed');
  };

  const reset = () => {
    setForm({ ...EMPTY, topic: initialTopic });
    setErrors({});
    setFailure(null);
    setState('idle');
  };

  const field = (name) => ({
    id: `contact-${name}`, name, value: form[name], onChange: set,
    'aria-invalid': errors[name] ? 'true' : 'false',
    'aria-describedby': errors[name] ? `contact-${name}-error` : undefined,
  });

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />

      <main className="flex-1">
        <section className="bg-white border-b">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16 sm:py-20 text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Contact us</h1>
            <p className="text-lg text-gray-500">
              Questions, problems, ideas or business enquiries — send us a message and we’ll reply by email.
            </p>
          </div>
        </section>

        <section className="max-w-5xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
            <div className="space-y-6">
              <Card className="border-0 shadow-sm">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                      <Mail className="w-4 h-4 text-purple-600" aria-hidden="true" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-gray-900 text-sm">Email us directly</p>
                      <a href={`mailto:${SUPPORT_EMAIL}`} className="text-purple-600 hover:underline text-sm break-all">{SUPPORT_EMAIL}</a>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <p className="text-xs text-gray-500 leading-relaxed px-1">
                For questions about your data, choose “Privacy and data requests”. See our{' '}
                <a href="/privacy" className="underline hover:text-gray-700">Privacy Policy</a> for how we handle messages.
              </p>
            </div>

            <div className="md:col-span-2">
              <Card className="border-0 shadow-sm">
                <CardContent className="pt-6">
                  {state === 'sent' ? (
                    <div className="flex flex-col items-center justify-center py-12 text-center gap-4">
                      <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center">
                        <CheckCircle className="w-7 h-7 text-green-600" aria-hidden="true" />
                      </div>
                      <h2 className="text-xl font-bold text-gray-900">Thanks — your message is on its way</h2>
                      <p className="text-gray-500 text-sm max-w-sm">We’ll reply to {sentTo}.</p>
                      <button type="button" onClick={reset} className="mt-2 h-10 px-4 rounded-lg border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50">
                        Send another message
                      </button>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} noValidate className="space-y-5">
                      {state === 'failed' && (
                        <div role="alert" className="flex gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" aria-hidden="true" />
                          <p>
                            {failure === 'rate'
                              ? 'Too many messages from your connection in a short time. Please wait a few minutes and try again, or email us at '
                              : failure === 'invalid'
                                ? 'Please check the form and try again, or email us at '
                                : 'We couldn’t send your message. Please try again, or email us at '}
                            <a href={`mailto:${SUPPORT_EMAIL}`} className="font-semibold underline">{SUPPORT_EMAIL}</a>.
                          </p>
                        </div>
                      )}

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <Field id="contact-name" label="Name" required error={errors.name}>
                          <input type="text" autoComplete="name" maxLength={100} placeholder="Your name" className={`${inputClass(errors.name)} h-10`} {...field('name')} />
                        </Field>
                        <Field id="contact-email" label="Email" required error={errors.email}>
                          <input type="email" autoComplete="email" placeholder="you@example.com" className={`${inputClass(errors.email)} h-10`} {...field('email')} />
                        </Field>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <Field id="contact-topic" label="Topic">
                          <select className={`${inputClass(false)} h-10 bg-white`} {...field('topic')}>
                            {TOPICS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
                          </select>
                        </Field>
                        <Field id="contact-company" label="Company" error={errors.company} hint="Optional">
                          <input type="text" autoComplete="organization" maxLength={120} className={`${inputClass(errors.company)} h-10`} {...field('company')} />
                        </Field>
                      </div>

                      <Field id="contact-message" label="Message" required error={errors.message}>
                        <textarea rows={6} maxLength={5000} placeholder="How can we help?" className={`${inputClass(errors.message)} py-2.5 resize-y`} {...field('message')} />
                      </Field>

                      {/* Honeypot: invisible to people and screen readers; bots fill it in. */}
                      <div aria-hidden="true" className="absolute -left-[9999px] w-px h-px overflow-hidden">
                        <label htmlFor="contact-website">Website</label>
                        <input id="contact-website" type="text" name="website" tabIndex={-1} autoComplete="off" value={form.website} onChange={set} />
                      </div>

                      <button
                        type="submit"
                        disabled={state === 'sending'}
                        className="w-full h-11 bg-purple-600 hover:bg-purple-700 disabled:opacity-70 text-white font-semibold rounded-lg flex items-center justify-center gap-2 transition-colors"
                      >
                        {state === 'sending'
                          ? <><Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> Sending…</>
                          : <>Send message <ArrowRight className="w-4 h-4" aria-hidden="true" /></>}
                      </button>

                      <p className="text-xs text-gray-400 text-center">
                        We’ll use your details to reply to you. See our{' '}
                        <a href="/privacy" className="underline hover:text-gray-600">Privacy Policy</a>.
                      </p>
                    </form>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default ContactPage;
